export const SERVICE_PAYLOAD = {
  BLOB: 'blob',
  JSON: 'json'
};

export const SERVICE_HOST = {
  PSI: 'psi.pixum.com',
  API_PSI: 'www.pixum.com'
} as const;

export const SERVICE_TYPE = {
  PSI: 'psi',
  API_PSI: 'api_psi',
  AUTH: 'auth',
  CREATION: 'creation',
  IMAGE_SERVICE: 'image_service',
  ADDRESS_SERVICE: 'address_service'
} as const;

/**
 * Auth service configuration
 * Used by Server and Client JWT managers
 */
export const AUTH_SERVICE = {
  HOST: process.env['NEXT_PIXUM_AUTH_HOST'] || 'https://auth-service.pixum-api.com',
  PATH: '/api/auth-service',
  ENDPOINTS: {
    LOGIN_ANONYMOUS: '/api/login/anonymous',
    LOGIN_CUSTOMER: '/api/login/customer',
    REFRESH_CUSTOMER_TOKEN: '/api/token/refreshCustomerToken',
    SIGNUP_CUSTOMER: '/api/signup/customer',
    SIGNUP_CUSTOMER_CART: '/api/signup/customer/cart'
  }
} as const;

/**
 * Headers for server-side auth service calls.
 *
 * Server-to-server calls bypass the ingress, whose proxy-cookie-path gives the refreshToken cookie the BFF
 * path for browser calls. The auth service builds the same Path itself when it receives the origin base path
 * (the HA Proxy sends it for browser traffic), so the cookies can be forwarded unchanged.
 * The value must be exactly AUTH_SERVICE.PATH: the auth service concatenates it as-is.
 */
export const AUTH_SERVICE_ORIGIN_HEADERS = {
  'x-origin-base-path': AUTH_SERVICE.PATH
} as const;

export const IMAGE_SERVICE = {
  PATH: '/api/image-service',
  TYPE: 'image-service',
  ENDPOINTS: {
    UPLOAD_URL: '/api/uploads/url',
    LATEST_IMAGES: '/api/images/latest',
    VALIDATE: '/api/uploads/validate',
    MOVE_TO_TRASH: '/api/images/moveToTrash',
    FACES: '/api/images/:imageId/faces',
    THUMB_BIG_URL: (imageId: string) => `/api/images/${imageId}/thumb/big/url`,
    THUMB_SMALL_URL: (imageId: string) => `/api/images/${imageId}/thumb/small/url`
  }
} as const;

/**
 * Creation service configuration
 * Used by Server and Client JWT managers
 */
export const CREATION_SERVICE = {
  HOST: process.env['NEXT_PIXUM_CREATION_HOST'] || 'https://creation-service.pixum-api.com',
  PATH: '/api/creation-service',
  ENDPOINTS: {
    CREATION_LEGACY: (creationId: number) => `/api/creations/${creationId}/legacy`,
    CREATION_OPTIONS_LEGACY: (creationId: number) => `/api/creations/${creationId}/legacy/options`,
    CREATION_METADATA_LEGACY: (creationId: number) => `/api/creations/${creationId}/legacy/compute-metadata`
  }
} as const;

/**
 * Address service configuration
 */
export const ADDRESS_SERVICE = {
  HOST: process.env['NEXT_PIXUM_ADDRESS_HOST'] ?? '',
  PATH: '/api/address-service',
  ENDPOINTS: {
    VALIDATE: '/api/validate'
  }
} as const;

/**
 * Service URL mapping for both client and server contexts
 *
 * CLIENT URLs:
 * - Use relative paths through Next.js API routes to avoid CORS
 * - Proxied by Next.js to actual service endpoints
 *
 * SERVER URLs:
 * - Use absolute URLs for direct service calls
 * - No CORS restrictions in server-to-server communication
 *
 * Easily extensible for new microservices - add entries to both CLIENT and SERVER maps
 */
export const SERVICE_URL_MAP = {
  CLIENT: {
    [SERVICE_TYPE.PSI]: '/api/psi-service',
    [SERVICE_TYPE.API_PSI]: '/api/api-psi-service',
    [SERVICE_TYPE.AUTH]: AUTH_SERVICE.PATH,
    [SERVICE_TYPE.CREATION]: CREATION_SERVICE.PATH,
    [SERVICE_TYPE.IMAGE_SERVICE]: IMAGE_SERVICE.PATH,
    [SERVICE_TYPE.ADDRESS_SERVICE]: ADDRESS_SERVICE.PATH
  },
  SERVER: {
    [SERVICE_TYPE.PSI]: process.env['NEXT_PIXUM_PSI_HOST'] || `https://${SERVICE_HOST.PSI}`,
    [SERVICE_TYPE.API_PSI]: process.env['NEXT_PIXUM_PSI_HOST'] || `https://${SERVICE_HOST.API_PSI}`,
    [SERVICE_TYPE.AUTH]: AUTH_SERVICE.HOST,
    [SERVICE_TYPE.CREATION]: CREATION_SERVICE.HOST,
    [SERVICE_TYPE.IMAGE_SERVICE]:
      process.env['NEXT_PIXUM_IMAGE_SERVICE_HOST'] || 'https://www-stage-mcy1.pixum.net/api/image-service',
    [SERVICE_TYPE.ADDRESS_SERVICE]: ADDRESS_SERVICE.HOST
  }
} as const;
